import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useDataProvider,
  useGetList,
  useNotify,
  useRefresh,
  useTranslate,
} from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

import type { CrmDataProvider } from "../providers/types";
import { QUOTER_QUERY_KEY } from "../quoter/QuoteBuilder";
import type { OperationSummary, Sale } from "../types";
import {
  OperationExpedienteGuard,
  useOperationExpedienteGuard,
} from "./OperationExpedienteGuard";
import type { OperationControls } from "./operationRules";
import { previewFolio, todayIso } from "./operationRules";

type ActionKind = "sign" | "cancel" | "advisor";

/**
 * Almikia Epic E4 (TASK-027): sign / cancel / change-advisor actions of an
 * operation. Each opens a confirmation that explains the consequences in
 * plain language; the provider's Spanish error is shown inline. Only the
 * actions allowed by `controls` render.
 */
export const OperationActions = ({
  operation,
  controls,
}: {
  operation: OperationSummary;
  controls: OperationControls;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [kind, setKind] = useState<ActionKind | null>(null);
  const [reason, setReason] = useState("");
  const [signedOn, setSignedOn] = useState(todayIso());
  const [newSalesId, setNewSalesId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: sales } = useGetList<Sale>(
    "sales",
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "first_name", order: "ASC" },
    },
    { enabled: controls.canChangeAdvisor },
  );

  const open = (next: ActionKind) => {
    setKind(next);
    setError(null);
    setReason("");
    setNewSalesId("");
    setSignedOn(todayIso());
  };

  // Corretaje (option_id set): no folio, no unit text; the copy talks about the option.
  const isCorretaje = operation.option_id != null;
  const variant = (key: ActionKind) =>
    isCorretaje && key !== "advisor" ? `${key}_corretaje` : key;
  const folio = previewFolio(
    operation.project_code,
    operation.unit_identifier,
    signedOn,
  );
  const needsReason = kind === "cancel" || kind === "advisor";
  // EC2b (TASK-125): signing is offered only when every buyer contact has a complete expediente
  // (the SQL stays the authority; a server refusal is still shown as-is).
  const expedienteGuard = useOperationExpedienteGuard(
    operation.id,
    kind === "sign",
  );
  const canConfirm =
    !busy &&
    (kind === "sign"
      ? expedienteGuard.isClear && (isCorretaje || folio != null)
      : reason.trim() !== "" && (kind !== "advisor" || newSalesId !== ""));

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      if (kind === "sign") {
        await dataProvider.signOperation(operation.id, signedOn);
      } else if (kind === "cancel") {
        await dataProvider.cancelOperation(operation.id, reason.trim());
      } else if (kind === "advisor") {
        await dataProvider.changeOperationAdvisor(
          operation.id,
          Number(newSalesId),
          reason.trim(),
        );
      }
      await queryClient.invalidateQueries({ queryKey: [QUOTER_QUERY_KEY] });
      notify(`crm.operations.done.${kind ? variant(kind) : ""}`, {
        type: "success",
      });
      setKind(null);
      refresh();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : translate("crm.operations.error"),
      );
    } finally {
      setBusy(false);
    }
  };

  if (!controls.canSign && !controls.canCancel && !controls.canChangeAdvisor) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {controls.canSign ? (
        <Button
          type="button"
          variant="gradient"
          className="h-11 sm:h-9"
          onClick={() => open("sign")}
        >
          {translate(`crm.operations.actions.${variant("sign")}`)}
        </Button>
      ) : null}
      {controls.canChangeAdvisor ? (
        <Button
          type="button"
          variant="outline"
          className="h-11 sm:h-9"
          onClick={() => open("advisor")}
        >
          {translate("crm.operations.actions.change_advisor")}
        </Button>
      ) : null}
      {controls.canCancel ? (
        <Button
          type="button"
          variant="outline"
          className="h-11 sm:h-9"
          onClick={() => open("cancel")}
        >
          {translate("crm.operations.actions.cancel")}
        </Button>
      ) : null}

      <Dialog open={kind != null} onOpenChange={(o) => !o && setKind(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {kind
                ? translate(`crm.operations.confirm.${variant(kind)}.title`)
                : ""}
            </DialogTitle>
            <DialogDescription>
              {kind
                ? translate(
                    `crm.operations.confirm.${variant(kind)}.description`,
                  )
                : ""}
            </DialogDescription>
          </DialogHeader>
          {kind === "sign" ? (
            <OperationExpedienteGuard guard={expedienteGuard} />
          ) : null}
          {kind === "sign" ? (
            <div className="space-y-2">
              <label className="block text-sm font-medium" htmlFor="signed-on">
                {translate("crm.operations.fields.signed_on")}
              </label>
              <input
                id="signed-on"
                type="date"
                value={signedOn}
                onChange={(event) => setSignedOn(event.target.value)}
                className="h-11 w-full rounded-md border bg-background px-3 text-sm sm:h-9"
              />
              {isCorretaje ? null : (
                <p className="text-sm">
                  {translate("crm.operations.folio_preview")}{" "}
                  <strong data-testid="folio-preview" className="tabular-nums">
                    {folio ?? "—"}
                  </strong>
                </p>
              )}
            </div>
          ) : null}
          {kind === "advisor" ? (
            <div className="space-y-2">
              <label
                className="block text-sm font-medium"
                htmlFor="new-advisor"
              >
                {translate("crm.operations.fields.new_advisor")}
              </label>
              <select
                id="new-advisor"
                value={newSalesId}
                onChange={(event) => setNewSalesId(event.target.value)}
                className="h-11 w-full rounded-md border bg-background px-3 text-sm sm:h-9"
              >
                <option value="">
                  {translate("crm.operations.choose_advisor")}
                </option>
                {(sales ?? [])
                  .filter(
                    (sale) =>
                      sale.id !== operation.sales_id &&
                      !sale.disabled &&
                      (sale.role === "asesora" || sale.role === "lider"),
                  )
                  .map((sale) => (
                    <option key={sale.id} value={String(sale.id)}>
                      {sale.first_name} {sale.last_name}
                    </option>
                  ))}
              </select>
            </div>
          ) : null}
          {needsReason ? (
            <div className="space-y-2">
              <label className="block text-sm font-medium" htmlFor="reason">
                {translate("crm.operations.fields.reason")}
              </label>
              <Textarea
                id="reason"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </div>
          ) : null}
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="h-11 sm:h-9"
              onClick={() => setKind(null)}
            >
              {translate("crm.operations.confirm.back")}
            </Button>
            <Button
              type="button"
              className="h-11 sm:h-9"
              disabled={!canConfirm}
              onClick={confirm}
            >
              {translate("crm.operations.confirm.accept")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
